import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { DashboardResponse } from '../api/types';
import { useApp } from '../state/AppContext';
import { EmptyRow, ExportMenu, StatusPill, downloadCsv, formatLongDate } from '../components/ui';

export function Dashboard() {
  const { reference, periodId, setPeriodId, bandColor } = useApp();
  const navigate = useNavigate();
  const [location, setLocation] = useState('all');

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', periodId, location],
    queryFn: () => api.get<DashboardResponse>('/api/dashboard', { periodId, location }),
  });

  const coverage =
    data?.dataFirstDay && data?.dataLastDay
      ? `${formatLongDate(data.dataFirstDay)} – ${formatLongDate(data.dataLastDay)}`
      : 'no records in this period yet';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Executive Dashboard</div>
          <div className="page-subtitle">{data?.scopeLabel ?? '…'} · read-only · data {coverage}</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select className="control" value={location} onChange={(e) => setLocation(e.target.value)} aria-label="Location">
            <option value="all">All locations</option>
            {reference.facilities.map((f) => <option key={f.code} value={f.code}>{f.name}</option>)}
          </select>
          {/* The app's single period control. Changing it here rescopes the
              scorecard, both date filters and the employee feed too. */}
          <select className="control" value={periodId} onChange={(e) => setPeriodId(Number(e.target.value))} aria-label="Period">
            {reference.periods.map((p) => (
              <option key={p.periodId} value={p.periodId}>
                {p.isCurrent ? 'Current' : p.status === 'locked' ? 'Closed' : 'Upcoming'} — {p.name}
              </option>
            ))}
          </select>
          <ExportMenu
            onExport={() =>
              downloadCsv(
                `dashboard-${data?.scopeLabel ?? 'all'}.csv`,
                ['Rank (1 = best)', 'Employee ID', 'Employee', 'Location', 'Score', 'Status', 'Deductions'],
                (data?.lowestScores ?? []).map((r) => [r.rank, r.employeeId, r.name, r.locationCode, r.score, r.status, r.deductions]),
              )
            }
          />
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat">
          <div className="stat__label">AVERAGE SCORE</div>
          <div className="stat__value">{data?.averageScore ?? '—'}</div>
        </div>
        {/* Clicking navigates to the scorecard carrying its filter, which
            arrives there as a removable chip rather than a silent pre-filter. */}
        <div
          className="stat stat--clickable"
          onClick={() => navigate(`/scorecard?maxScore=90${location !== 'all' ? `&location=${location}` : ''}`)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && navigate(`/scorecard?maxScore=90`)}
        >
          <div className="stat__label">BELOW GOOD STANDING</div>
          <div className="stat__value" style={{ color: 'var(--red)' }}>{data?.belowGoodStandingCount ?? '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--blue)', fontWeight: 600, marginTop: 4 }}>View employees →</div>
        </div>
        <div className="stat">
          <div className="stat__label">TOTAL EMPLOYEES</div>
          <div className="stat__value">{data?.totalEmployees ?? '—'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: 16, marginBottom: 22 }}>
        <div className="card">
          <div className="section-label" style={{ marginBottom: 14 }}>STANDINGS</div>
          {(data?.standings ?? []).map((s) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 9 }}>
              <div style={{ width: 150, fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</div>
              <div className="bar-track">
                <div className="bar-fill" style={{ width: `${s.percent}%`, background: s.color }} />
              </div>
              <div style={{ width: 34, textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>
                {s.count}
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="section-label" style={{ marginBottom: 14 }}>BY LOCATION</div>
          {/* Scores come from the facility database, department and location
              from HR — this list is where the two get stitched, so it is the
              slowest thing on the screen. */}
          {isLoading && <div className="meta">Loading breakdown…</div>}
          {(data?.byLocation ?? []).map((l) => (
            <div
              key={l.code}
              onClick={() => setLocation(l.code)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid var(--rule)', cursor: 'pointer',
              }}
            >
              <div style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 600 }}>{l.name}</div>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span className="meta">{l.employeeCount} emp.</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: l.averageScore < 90 ? 'var(--orange)' : 'var(--green)' }}>
                  {l.averageScore}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Layout slot for the Phase 2 rolling trend chart. Reserved on purpose:
          the card exists, the chart does not. */}
      <div
        style={{
          border: '2px dashed var(--dashed)', borderRadius: 'var(--radius-card)',
          padding: '22px 24px', marginBottom: 22, color: 'var(--muted)', fontSize: 12.5,
        }}
      >
        <strong style={{ color: 'var(--note)' }}>Rolling score trend</strong> — Phase 2. Every period is archived rather
        than overwritten, so the history this chart needs is already accumulating.
      </div>

      <div className="table-wrap">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="section-label" style={{ marginBottom: 0 }}>
            EMPLOYEES WITH DEDUCTIONS — {(data?.scopeLabel ?? 'ALL LOCATIONS').toUpperCase()}
          </div>
          <button className="btn btn--sm" style={{ border: 'none', background: 'transparent', color: 'var(--blue)' }}
            onClick={() => navigate('/scorecard')}>
            View full scorecard summary →
          </button>
        </div>
        <table className="table">
          <thead>
            <tr>
              <th>RANK</th>
              <th className="num">ID</th>
              <th>EMPLOYEE</th>
              <th>LOCATION</th>
              <th>STATUS</th>
              <th className="num">SCORE</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {isLoading && <EmptyRow colSpan={7}>Loading…</EmptyRow>}
            {!isLoading && (data?.lowestScores.length ?? 0) === 0 && (
              <EmptyRow colSpan={7}>
                Every employee is at 100 for {reference.periods.find((p) => p.periodId === periodId)?.name}. Points count
                only once a Daily Summary flag is approved, so a fresh import shows a clean board until review starts.
              </EmptyRow>
            )}
            {(data?.lowestScores ?? []).map((r) => {
              const band = bandColor(r.status);
              return (
                <tr key={r.employeeId} className="clickable" onClick={() => navigate(`/employee/${r.employeeId}`)}>
                  <td style={{ whiteSpace: 'nowrap', color: 'var(--muted)' }}>{r.rank} of {data?.rankedCount}</td>
                  <td className="num">{r.employeeId}</td>
                  <td className="strong">{r.name}</td>
                  <td className="meta">{r.locationCode}</td>
                  <td><StatusPill label={r.status} color={band.color} bg={band.bg} /></td>
                  <td className="num" style={{ fontSize: 14, fontWeight: 700, color: band.color }}>{r.score}</td>
                  <td className="num" style={{ color: 'var(--blue)', fontWeight: 600 }}>View →</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
